import dayjs from 'dayjs';

import type { MultiSelectValue } from '@app/shared/components/MultiSelect/types';
import type { NRecords } from '@app/types';

import type { Dayjs } from 'dayjs';

// Применённые фильтры реестра записей переживают уход на карточку записи или
// клиента: при возврате страница монтируется заново и без хранилища поиск
// стартовал бы с фильтров по умолчанию (issue/11073). Хранилище — вкладочное
// (sessionStorage, решение Михаила 06.09.2026): фильтры живут ровно столько,
// сколько открыта вкладка, а новая вкладка и новый сеанс начинают с
// умолчаний. В реестре лидов ключ аналогичный, но там хранилище постоянное
// (`useLeadsRegistryFilters.ts`, `leads-v2:registry:filters`).
const STORAGE_KEY = 'records:registry:filters';

export type StoredRecordsSearchRequest = Omit<
  NRecords.ModelRecordSearchFilter,
  'RecordsPerPage' | 'DateCreateS' | 'DateCreateE'
> & {
  DateCreateS: string | null;
  DateCreateE: string | null;
};

export interface StoredRecordsRegistryFilters {
  // Ровно тот фильтр, по которому в прошлый раз выполнился поиск: из него
  // собирается и стартовый запрос, и значения контролов.
  request: StoredRecordsSearchRequest;
  // Мультиселекты тегов хранят собственную структуру групп, в запрос она
  // уходит уже преобразованной в Int32ItemsFilter.
  recordTags: MultiSelectValue;
  clientTags: MultiSelectValue;
}

const isStoredRecordsRegistryFilters = (
  value: unknown
): value is StoredRecordsRegistryFilters => {
  if (typeof value !== 'object' || value === null) return false;

  const { request, recordTags, clientTags } =
    value as Partial<StoredRecordsRegistryFilters>;

  return (
    typeof request === 'object' &&
    request !== null &&
    typeof recordTags === 'object' &&
    recordTags !== null &&
    typeof clientTags === 'object' &&
    clientTags !== null
  );
};

export const readStoredRecordsFilters =
  (): StoredRecordsRegistryFilters | null => {
    try {
      const rawValue = sessionStorage.getItem(STORAGE_KEY);
      if (!rawValue) return null;

      const parsedValue: unknown = JSON.parse(rawValue);

      return isStoredRecordsRegistryFilters(parsedValue) ? parsedValue : null;
    } catch {
      return null;
    }
  };

export const saveStoredRecordsFilters = (
  value: StoredRecordsRegistryFilters
) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Приватный режим и переполненное хранилище не должны ломать поиск.
  }
};

export const clearStoredRecordsFilters = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // См. saveStoredRecordsFilters.
  }
};

// Диапазон RangePicker: сохранённые границы — строки формата запроса.
export const toStoredDateRange = (
  start: string | null,
  end: string | null
): [Dayjs | null, Dayjs | null] | null => {
  if (!start && !end) return null;

  const startDate = start ? dayjs(start) : null;
  const endDate = end ? dayjs(end) : null;

  return [
    startDate?.isValid() ? startDate : null,
    endDate?.isValid() ? endDate : null,
  ];
};

// Значения контролов-мультиселектов восстанавливаются сопоставлением
// сохранённых идентификаторов с уже загруженными опциями фильтра.
export const pickOptionsByValues = <
  Option extends { value?: string | number | null }
>(
  options: Option[],
  values: Array<string | number> | null
): Option[] => {
  if (!values?.length) return [];

  const storedValues = new Set(values.map((value) => String(value)));

  return options.filter(
    (option) => option.value != null && storedValues.has(String(option.value))
  );
};
